import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import AppManagementGuard from 'utils/route-guard/AppManagementGuard';
import AdminGuard from 'utils/route-guard/AdminGuard';
import MaintenanceGuard from 'utils/route-guard/MaintenanceGuard';
import { APP_MANAGEMENT_TABS, TenantTab } from 'utils/appManagementAccess';
import { ADMIN_TABS, CompanyTab, SitesTab, AttachedPeripheralsTab } from 'utils/adminAccess';
import { maintenanceActionPermissions } from 'utils/maintenanceAccess';
import PeripheralManagement from 'views/admin/PeripheralManagement';

// Dashboard & reporting routing
const Dashboard = Loadable(lazy(() => import('views/dashboard/Home')));
const ActivitySummary = Loadable(lazy(() => import('views/activity')));
const MyReports = Loadable(lazy(() => import('views/reporting-portal')));
const Notifications = Loadable(lazy(() => import('views/notifications')));
const StandardReports = Loadable(lazy(() => import('views/report')));
const AccountSettings = Loadable(lazy(() => import('views/account/AccountSettings')));

// Transaction routing
const Visits = Loadable(lazy(() => import('views/visits')));
const VisitDetail = Loadable(lazy(() => import('views/visits/VisitDetail')));
const OnsiteVehicles = Loadable(lazy(() => import('views/onsite-vehicles')));

// Master Data Maintenance routing
const Maintenance = Loadable(lazy(() => import('views/maintenance')));
const VehicleImport = Loadable(lazy(() => import('views/maintenance/vehicles/VehicleImport')));
const MaterialImport = Loadable(lazy(() => import('views/maintenance/materials/MaterialImport')));
const MaterialCategoryImport = Loadable(lazy(() => import('views/maintenance/materials/MaterialCategoryImport')));
const ContractImport = Loadable(lazy(() => import('views/maintenance/contracts/ContractImport')));
const CarrierImport = Loadable(lazy(() => import('views/maintenance/carriers/CarrierImport')));
const LotImport = Loadable(lazy(() => import('views/maintenance/lots/LotImport')));
const CustomerImport = Loadable(lazy(() => import('views/maintenance/customers/CustomerImport')));

// Invoicing module routing
const Invoicing = Loadable(lazy(() => import('views/invoicing')));

// Administration routing
const Admin = Loadable(lazy(() => import('views/admin')));
const CompanyManagement = Loadable(lazy(() => import('views/admin/CompanyManagement')));
const SiteManagement = Loadable(lazy(() => import('views/admin/SiteManagement')));
const AuditLog = Loadable(lazy(() => import('views/admin/AuditLog')));

// App management routing
const AppManagement = Loadable(lazy(() => import('views/app-management')));
const TenantManagement = Loadable(lazy(() => import('views/app-management/TenantManagement')));
const SyncEventsManagement = Loadable(lazy(() => import('views/app-management/SyncEventsManagement')));

// Support & content management routing
const CMS = Loadable(lazy(() => import('views/cms')));
const Support = Loadable(lazy(() => import('views/support')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <MainLayout />
    </AuthGuard>
  ),
  children: [
    {
      path: '/activity-summary',
      element: <ActivitySummary />
    },
    { path: '/dashboard', element: <Dashboard /> },
    { path: '/visits/add', element: <Visits /> },
    { path: '/onsite-vehicles', element: <OnsiteVehicles /> },
    // The site travels as a query parameter: a visit is addressed by site as well as id, and the
    // activity summary row that links here carries the one it belongs to.
    { path: '/visits/:id', element: <VisitDetail /> },
    { path: '/audit', element: <AuditLog /> },
    // The sidebar's Reports item (driven from the database) still links here.
    { path: '/reports', element: <Navigate to="/report/customers" replace /> },
    { path: '/my-reports', element: <MyReports /> },
    { path: '/notifications', element: <Notifications /> },
    // Standard reports: /report opens the first the user may run, /report/:code a specific one.
    { path: '/report', element: <StandardReports /> },
    { path: '/report/:code', element: <StandardReports /> },
    { path: '/account-settings', element: <AccountSettings /> },
    {
      path: '/admin',
      element: (
        <AdminGuard>
          <Admin />
        </AdminGuard>
      )
    },
    {
      path: '/admin/companies',
      element: (
        <AdminGuard requiredPermissions={ADMIN_TABS[CompanyTab].permissions}>
          <CompanyManagement />
        </AdminGuard>
      )
    },
    {
      path: '/admin/sites',
      element: (
        <AdminGuard requiredPermissions={ADMIN_TABS[SitesTab].permissions}>
          <SiteManagement />
        </AdminGuard>
      )
    },
    {
      path: '/admin/peripherals',
      element: (
        <AdminGuard requiredPermissions={ADMIN_TABS[AttachedPeripheralsTab].permissions}>
          <PeripheralManagement />
        </AdminGuard>
      )
    },
    {
      path: '/application-management',
      element: (
        <AppManagementGuard>
          <AppManagement />
        </AppManagementGuard>
      )
    },
    {
      path: '/application-management/tenants',
      element: (
        <AppManagementGuard requiredPermissions={APP_MANAGEMENT_TABS[TenantTab].permissions}>
          <TenantManagement />
        </AppManagementGuard>
      )
    },
    {
      path: '/application-management/sync-events',
      element: (
        <AppManagementGuard requiredPermissions={['manage:application']}>
          <SyncEventsManagement />
        </AppManagementGuard>
      )
    },
    {
      path: '/maintenance',
      element: (
        <MaintenanceGuard>
          <Maintenance />
        </MaintenanceGuard>
      )
    },
    {
      path: '/maintenance/:tab',
      element: (
        <MaintenanceGuard>
          <Maintenance />
        </MaintenanceGuard>
      )
    },
    {
      path: '/maintenance/vehicles/import',
      element: (
        <MaintenanceGuard requiredPermissions={maintenanceActionPermissions('vehicles', 'import')}>
          <VehicleImport />
        </MaintenanceGuard>
      )
    },
    {
      path: '/maintenance/customers/import',
      element: (
        <MaintenanceGuard requiredPermissions={maintenanceActionPermissions('customers', 'import')}>
          <CustomerImport />
        </MaintenanceGuard>
      )
    },
    {
      path: '/maintenance/materials/import',
      element: (
        <MaintenanceGuard requiredPermissions={maintenanceActionPermissions('materials', 'import')}>
          <MaterialImport />
        </MaintenanceGuard>
      )
    },
    {
      path: '/maintenance/lots/import',
      element: (
        <MaintenanceGuard requiredPermissions={maintenanceActionPermissions('lots', 'import')}>
          <LotImport />
        </MaintenanceGuard>
      )
    },
    {
      path: '/maintenance/carriers/import',
      element: (
        <MaintenanceGuard requiredPermissions={maintenanceActionPermissions('carriers', 'import')}>
          <CarrierImport />
        </MaintenanceGuard>
      )
    },
    {
      path: '/maintenance/contracts/import',
      element: (
        <MaintenanceGuard requiredPermissions={maintenanceActionPermissions('contracts', 'import')}>
          <ContractImport />
        </MaintenanceGuard>
      )
    },
    {
      path: '/maintenance/material-categories/import',
      element: (
        <MaintenanceGuard requiredPermissions={maintenanceActionPermissions('material-categories', 'import')}>
          <MaterialCategoryImport />
        </MaintenanceGuard>
      )
    },
    { path: '/invoicing', element: <Invoicing /> },
    { path: '/cms/editor', element: <CMS /> },
    { path: '/support', element: <Support /> }
  ]
};

export default MainRoutes;
